import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const stats = [
    { value: "843+", label: "Participants across ONGC" },
    { value: "372", label: "Teams Actively Engaged" },
    { value: "542", label: "Total Ideas Submitted" },
    { value: "₹22,400 Cr+", label: "Potential Business Value (5-yr est by participants)" },
    { value: "60%", label: "Increase Over Udbhav 1.0 Value Estimates" },
    { value: "20%", label: "Ideas submitted with groundwork already done" },
];

const themes = [
    {
        title: "AI Moving from Concept to Implementation",
        items: ["Early prototypes", "Data readiness", "Clear execution pathways"],
        accent: "#00e5a0",
        icon: "⚡",
    },
    {
        title: "Stronger Business Orientation",
        items: ["Clear ROI articulation", "Scalable impact across assets", "Alignment with ONGC's strategic priorities"],
        accent: "#4fc3f7",
        icon: "📈",
    },
    {
        title: "Rise of AI Across the Value Chain",
        items: ["Drilling optimization", "Reservoir intelligence", "Predictive maintenance", "Safety & HSSE monitoring", "Process plant optimization"],
        accent: "#ff7043",
        icon: "🔗",
    },
    {
        title: "Improved Execution Framework",
        items: ["Early POC stage introduced", "Faster evaluation cycles (~2 weeks)", "Defined roadmap at shortlisting stage"],
        accent: "#ab47bc",
        icon: "🛠",
    },
];

const whatsNext = [
    { step: "01", title: "Final Presentations (Demo Day)", desc: "Shortlisted teams showcase completed ideas" },
    { step: "02", title: "Prototype Development", desc: "4–6 weeks of intensive building" },
    { step: "03", title: "Scale & Deploy", desc: "Top use cases selected for full-scale deployment" },
    { step: "04", title: "Recognition", desc: "Teams recognized across categories" },
];

const gemCategories = [
    {
        gem: "💎 Diamond",
        color: "#a5f3fc",
        title: "High-impact, executable, transformational ideas",
        desc: "Ideas with a clear AI application, strong business case, and immediate implementation potential — the kind that can change how ONGC operates at scale.",
    },
    {
        gem: "🔴 Ruby",
        color: "#fca5a5",
        title: "Sharp, relevant, and practically ONGC-focused",
        desc: "Well-defined ideas with strong operational grounding that are almost ready to run and clearly pointed in the right direction with real business relevance.",
    },
    {
        gem: "🔷 Sapphire",
        color: "#93c5fd",
        title: "Strong foundation, needs shaping",
        desc: "Good instincts and solid problem identification, may need further development of the AI application or business case to reach full potential.",
    },
    {
        gem: "💚 Emerald",
        color: "#86efac",
        title: "Everyday problems solved smartly",
        desc: "Practical, on-the-ground ideas that address real operational friction — incremental in scope but meaningful in impact, and grounded in daily reality.",
    },
    {
        gem: "🟡 Topaz",
        color: "#fde68a",
        title: "Innovative, needing some refinement",
        desc: "Creative thinking with genuine potential — ideas that show ambition and curiosity, and can become really useful with some work on feasibility, scoping, or AI application design.",
    },
    {
        gem: "🟤 Jasper",
        color: "#d6b4a0",
        title: "Out-of-the-box thinking",
        desc: "Ideas that challenge conventional boundaries: exploratory, unconventional, and worth preserving as seeds for future innovation even if not immediately executable.",
    },
    {
        gem: "🟠 Amber",
        color: "#fdba74",
        title: "Acknowledgement for participation",
        desc: "Every idea submitted reflects initiative, courage, and a belief in ONGC's AI future. Every contribution counts and it is recognized as one.",
    },
];

const locationData = [
    { city: "Mumbai", count: "103 ideas" },
    { city: "Delhi/Noida", count: "48 ideas" },
    { city: "Rajahmundry", count: "51 ideas" },
    { city: "Ahmedabad", count: "50 ideas" },
    { city: "Dehradun", count: "35 ideas" },
    { city: "Mehsana", count: "36 ideas" },

];

function CountUp({ end, duration = 1800 }) {
    const [count, setCount] = useState(0);
    useEffect(() => {
        const isNum = !isNaN(parseFloat(end.replace(/[^0-9.]/g, "")));
        if (!isNum) { setCount(end); return; }
        const numStr = end.replace(/[^0-9.]/g, "");
        const num = parseFloat(numStr);
        const prefix = end.startsWith("₹") ? "₹" : "";
        const suffix = end.replace(numStr, "").replace(prefix, "");
        let start = 0;
        const step = num / (duration / 16);
        const timer = setInterval(() => {
            start += step;
            if (start >= num) { setCount(end); clearInterval(timer); return; }
            setCount(prefix + Math.floor(start).toLocaleString() + suffix);
        }, 16);
        return () => clearInterval(timer);
    }, [end]);
    return <span>{count || end}</span>;
}

export default function UdbhavPage() {
    const [visible, setVisible] = useState(false);
    useEffect(() => { setTimeout(() => setVisible(true), 100); }, []);

    const navigate = useNavigate();
    const location = useLocation();
    const activeTab = location.pathname === "/article"
        ? "article"
        : location.pathname === "/shortlisted"
            ? "shortlisted"
            : location.pathname === "/finalists"
                ? "finalists"
                : location.pathname === "/winners"
                    ? "winners"
                    : "submission-insights";

    const handleTabClick = (tab) => {
        if (tab === "article") {
            navigate("/article");
        } else if (tab === "shortlisted") {
            navigate("/shortlisted");
        } else if (tab === "finalists") {
            navigate("/finalists");
        } else if (tab === "winners") {
            navigate("/winners");
        } else {
            navigate("/submission-insights");
        }
    };

    return (
        <div style={{
            fontFamily: "system-ui, -apple-system, sans-serif",
            background: "#000000",
            color: "#e8f5e9",
            minHeight: "100vh",
            overflowX: "hidden",
        }}>
            <style>{`
        * { box-sizing: border-box; }
        body { background-color: #000000; margin: 0; }
        ::selection { background: #00e5a033; }
        .fade-in { opacity: 0; transform: translateY(28px); transition: opacity 0.8s ease, transform 0.8s ease; }
        .fade-in.visible { opacity: 1; transform: none; }
        .stat-card:hover { transform: translateY(-4px) scale(1.02); }
        .theme-card:hover { border-color: var(--accent) !important; }
        img { max-width: 100%; height: auto; }

        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 12px !important;
          }
          .two-col-grid {
            grid-template-columns: 1fr !important;
            gap: 32px !important;
          }
          .themes-grid {
            grid-template-columns: 1fr !important;
          }
          .gem-grid {
            grid-template-columns: 1fr !important;
          }
          .locations-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }

        @media (max-width: 480px) {
          .stats-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

            {/* HERO */}
            <header
                style={{
                    padding: "20px 16px 24px",
                    marginTop: "80px",
                    minHeight: "auto",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    position: "relative",
                    background: "#000000", // Pure black
                    borderBottom: "1px solid #1a1a1a",
                }}
            >

                {/* Tabs */}
                <div className="flex justify-center mb-2 w-full">
                    <div className="flex flex-col sm:flex-row gap-2 bg-white/5 p-2 sm:p-1 rounded-2xl sm:rounded-full border border-white/10 w-[92%] sm:w-fit">
                        <button
                            onClick={() => handleTabClick("submission-insights")}
                            className={`flex-1 whitespace-nowrap px-4 sm:px-6 py-2.5 sm:py-2 text-sm font-semibold rounded-full transition-all duration-200 ${activeTab === "submission-insights"
                                ? "bg-green-500 text-black shadow-md"
                                : "text-gray-300 hover:text-white hover:bg-white/10"
                                }`}
                        >
                            Submission Insights
                        </button>

                        <button
                            onClick={() => handleTabClick("article")}
                            className={`flex-1 whitespace-nowrap px-4 sm:px-6 py-2.5 sm:py-2 text-sm font-semibold rounded-full transition-all duration-200 ${activeTab === "article"
                                ? "bg-green-500 text-black shadow-md"
                                : "text-gray-300 hover:text-white hover:bg-white/10"
                                }`}
                        >
                            Evaluation Insights And Result
                        </button>

                        <button
                            onClick={() => handleTabClick("shortlisted")}
                            className={`flex-1 whitespace-nowrap px-4 sm:px-6 py-2.5 sm:py-2 text-sm font-semibold rounded-full transition-all duration-200 ${activeTab === "shortlisted"
                                ? "bg-green-500 text-black shadow-md"
                                : "text-gray-300 hover:text-white hover:bg-white/10"
                                }`}
                        >
                            Shortlisted Use Cases
                        </button>

                        <button
                            onClick={() => handleTabClick("finalists")}
                            className={`flex-1 whitespace-nowrap px-4 sm:px-6 py-2.5 sm:py-2 text-sm font-semibold rounded-full transition-all duration-200 ${activeTab === "finalists"
                                ? "bg-green-500 text-black shadow-md"
                                : "text-gray-300 hover:text-white hover:bg-white/10"
                                }`}
                        >
                            Meet the Finalists
                        </button>

                        <button
                            onClick={() => handleTabClick("winners")}
                            className={`flex-1 whitespace-nowrap px-4 sm:px-6 py-2.5 sm:py-2 text-sm font-semibold rounded-full transition-all duration-200 ${activeTab === "winners"
                                ? "bg-green-500 text-black shadow-md"
                                : "text-gray-300 hover:text-white hover:bg-white/10"
                                }`}
                        >
                            Meet the Winners
                        </button>
                    </div>
                </div>
                {/* Heading */}
                {/* Heading */}
                <h1
                    className="text-2xl sm:text-3xl md:text-4xl font-extrabold mb-4 sm:mb-6 bg-gradient-to-r from-green-400 via-emerald-400 to-red-400 bg-clip-text text-transparent tracking-tight leading-tight"
                >
                    Udbhav 2.0: From Ideas to Impact:
                    <br />
                    A New Benchmark in AI-Led Innovation

                    at ONGC
                </h1>

                {/* Stats Line */}
                <div
                    style={{
                        fontSize: "clamp(0.95rem, 2vw, 1.05rem)",
                        color: "#9ca3af",
                        marginBottom: "18px",
                        padding: "0 16px",
                        lineHeight: 1.6,
                    }}
                >
                    <span style={{ color: "#4ade80", fontWeight: "600" }}>843 participants</span>
                    {" · "}
                    <span style={{ color: "#4ade80", fontWeight: "600" }}>542 ideas submitted</span>
                    {" · "}
                    <span style={{ color: "#4ade80", fontWeight: "600" }}>372 unique teams</span>
                </div>

                {/* Description */}
                <p
                    style={{
                        fontSize: "clamp(0.95rem, 2vw, 1.1rem)",
                        color: "#9ca3af",
                        maxWidth: "700px",
                        lineHeight: 1.6,
                        margin: "0 0 20px",
                        padding: "0 16px",
                    }}
                >


                </p>


            </header>

            {/* MAIN CONTENT AREA */}
            <main style={{
                maxWidth: "1100px",
                margin: "0 auto",
                padding: "0 16px",
                background: "#000000",
            }}>

                {/* INTRO PARAGRAPH */}
                <section style={{ padding: "32px 0 16px" }}>
                    <div style={{
                        background: "#0a0a0a",
                        border: "1px solid #1a1a1a",
                        borderRadius: "12px",
                        padding: "28px",
                        position: "relative",
                    }}>
                        <div style={{
                            position: "absolute", top: 0, left: 0, right: 0, height: "2px",
                            background: "linear-gradient(to right, #4ade80, #10b981)",
                        }} />
                        <p style={{ fontSize: "1rem", lineHeight: 1.6, color: "#d1d5db", margin: "0 0 14px" }}>
                            When Udbhav 2.0 opened its doors on <strong style={{ color: "#fff" }}>January 9, 2026</strong>, we invited ONGCians across the organization to do one thing: look at the problems in front of them every day and imagine what AI could do about it. What came back exceeded every expectation.
                        </p>
                        <p style={{ fontSize: "1rem", lineHeight: 1.6, color: "#d1d5db", margin: "0 0 14px" }}>
                            In just <strong style={{ color: "#4ade80" }}>9 days</strong>, 843 employees across assets, basins, and corporate functions submitted 542 ideas — with <strong style={{ color: "#fff" }}>454 new submissions</strong> and <strong style={{ color: "#fff" }}>88 resubmissions</strong> that teams had refined and strengthened. Participation came from across all locations, departments, and levels. This was not enthusiasm from a few energized pockets. <strong style={{ color: "#fff" }}>This was ONGC-wide.</strong>
                        </p>
                        <div style={{ borderLeft: "3px solid #4ade80", paddingLeft: "16px" }}>
                            <p style={{ fontSize: "1rem", fontStyle: "italic", color: "#86efac", margin: 0 }}>
                                In 9 days, ONGCians submitted more than one idea every 25 minutes — around the clock.
                            </p>
                        </div>
                    </div>
                    {/* Original image */}
                    <div style={{
                        background: "#000000",
                        border: "1px solid #1e3a1e",
                        borderRadius: "16px",
                        padding: "16px",
                        overflow: "hidden",
                        marginBottom: "16px",
                    }}>
                        <img src="/images/image1artical.png" alt="Participant to Ideas Flow Diagram" style={{ width: "100%", borderRadius: "8px" }} />
                    </div>
                </section>


                {/* SNAPSHOT STATS */}
                <section style={{ padding: "24px 0" }}>
                    <div style={{ textAlign: "center", marginBottom: "24px" }}>
                        {/* <span style={{ fontSize: "14px", letterSpacing: "3px", color: "#f87171", fontWeight: "bold" }}>A Snapshot</span> */}
                        <h2 style={{
                            fontSize: "clamp(1.8rem, 5vw, 2.5rem)",
                            fontWeight: 800,
                            lineHeight: 1.1,
                            margin: "8px 0 0",
                            background: "linear-gradient(to right, #4ade80, #10b981, #f87171)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            letterSpacing: "-0.02em",
                        }}>
                            A Snapshot of Udbhav 2.0
                        </h2>
                    </div>

                    <div className="stats-grid" style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: "16px",
                        marginTop: "20px",
                    }}>
                        {stats.map((s, i) => (
                            <div key={i} className="stat-card" style={{
                                background: "#0a0a0a",
                                border: "1px solid #1a1a1a",
                                borderRadius: "8px",
                                padding: "20px 12px",
                                textAlign: "center",
                                position: "relative",
                                overflow: "hidden",
                                transition: "all 0.3s ease",
                            }}>
                                <div style={{
                                    position: "absolute",
                                    top: 0, left: 0, right: 0, height: "2px",
                                    background: i === 2 ? "#f87171" : "#4ade80",
                                }} />
                                <div style={{
                                    fontSize: "clamp(1.2rem, 2.5vw, 1.5rem)",
                                    fontWeight: 800,
                                    color: i === 2 ? "#f87171" : "#ffffff",
                                    lineHeight: 1,
                                    marginBottom: "8px",
                                }}>
                                    <CountUp end={s.value} />
                                </div>
                                <div style={{
                                    fontSize: "0.75rem",
                                    color: "#e6e7eb",
                                    letterSpacing: "0.5px",
                                    fontWeight: "600",
                                }}>
                                    {s.label}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* PARTICIPATION FLOW — original image + funnel bars */}
                <section style={{ padding: "32px 0" }}>





                </section>
                {/* WHAT ONGCIANS BROUGHT TO THE TABLE */}
                {/* WHAT ONGCIANS BROUGHT TO THE TABLE */}
                <section style={{ padding: "32px 0" }}>
                    <div style={{ textAlign: "center", marginBottom: "28px" }}>
                        <h2
                            style={{
                                fontSize: "clamp(1.5rem, 3vw, 2.2rem)",
                                fontWeight: 800,
                                margin: "8px 0 0",
                                color: "#4ade80",
                                letterSpacing: "-0.02em",
                            }}
                        >
                            What ONGCians Brought to the Table
                        </h2>
                    </div>

                    <div
                        style={{
                            background: "#0a0a0a",
                            border: "1px solid #1a1a1a",
                            borderRadius: "12px",
                            padding: "28px",
                        }}
                    >
                        <p
                            style={{
                                fontSize: "1rem",
                                lineHeight: 1.8,
                                color: "#d1d5db",
                                marginBottom: "18px",
                            }}
                        >
                            The ideas covered the full breadth of ONGC&apos;s operations: from the
                            wellhead to the boardroom. The majority came from domains like{" "}
                            <strong style={{ color: "#a5d6a7" }}>
                                Procurement, Drilling, and Process Plant
                            </strong>
                            , reflecting where operational complexity and opportunity are both
                            highest. But the more remarkable story was what appeared at the edges.
                        </p>

                        <p
                            style={{
                                fontSize: "1rem",
                                lineHeight: 1.8,
                                color: "#d1d5db",
                                marginBottom: "18px",
                            }}
                        >
                            Ideas arrived from functions like{" "}
                            <strong style={{ color: "#a5d6a7" }}>
                                Vigilance, Legal, CSR, and Green Hydrogen
                            </strong>
                            , functions that rarely feature in conversations about AI adoption. Their
                            presence here signals something important: the belief that AI can be a
                            tool for transformation is{" "}
                            <strong style={{ color: "#ffffff" }}>
                                no longer confined to technical teams
                            </strong>
                            . It has reached every corner of the organization.
                        </p>

                        <p
                            style={{
                                fontSize: "1rem",
                                lineHeight: 1.8,
                                color: "#d1d5db",
                                marginBottom: "18px",
                            }}
                        >
                            Geographically too, submissions came from every major location across the
                            country:{" "}
                            <strong style={{ color: "#b62121ff" }}>
                                Mumbai, Rajahmundry, Mehsana, Ahmedabad, Dehradun, Delhi, Jorhat,
                                Kakinada, Karaikal, Ankleshwar
                            </strong>
                            , and many more.{" "}
                            <strong style={{ color: "#ffffff" }}>
                                Udbhav 2.0 was truly national in its reach.
                            </strong>
                        </p>
                    </div>

                    {/* Original map image */}
                    <div
                        style={{
                            background: "#000000",
                            border: "1px solid #1e3a1e",
                            borderRadius: "12px",
                            padding: "16px",
                            marginTop: "24px",
                        }}
                    >
                        <img
                            src="/images/image3artical.png"
                            alt="India Map showing ONGC location-wise idea distribution"
                            style={{ width: "100%", borderRadius: "8px" }}
                        />
                    </div>
                </section>
                {/* EVALUATION SECTION */}
                <section style={{ padding: "32px 0", borderTop: "1px solid #111" }}>
                    <div style={{ textAlign: "center", marginBottom: "28px" }}>
                        <h2
                            style={{
                                fontSize: "clamp(1.5rem, 3vw, 2.2rem)",
                                fontWeight: 800,
                                margin: "8px 0 0",
                                color: "#4ade80",
                                letterSpacing: "-0.02em",
                            }}
                        >
                            The Evaluation: Rigorous and Thorough
                        </h2>
                    </div>

                    <div
                        style={{
                            background: "#0a0a0a",
                            border: "1px solid #1a1a1a",
                            borderRadius: "12px",
                            padding: "28px",
                        }}
                    >
                        <p
                            style={{
                                fontSize: "1rem",
                                lineHeight: 1.8,
                                color: "#d1d5db",
                                marginBottom: "18px",
                            }}
                        >
                            Once submissions closed, the Udbhav team began a structured,
                            comprehensive evaluation of all 542 ideas. This was not a quick scoring
                            exercise. Every submission was reviewed carefully for the clarity of the
                            problem it addressed, the relevance of the proposed AI application, and
                            the potential business value it could unlock for ONGC.
                        </p>

                        <p
                            style={{
                                fontSize: "1rem",
                                lineHeight: 1.8,
                                color: "#d1d5db",
                                marginBottom: "18px",
                            }}
                        >
                            The process was designed to give every idea a fair hearing. Udbhav
                            organizing team held one-on-one discussions for each idea and found that
                            the overall quality of submissions in Udbhav 2.0 was significantly good.
                            Stronger problem definitions. More grounded AI applications.
                            Better-articulated business cases. ONGCians came prepared and it showed.
                        </p>

                        <p
                            style={{
                                fontSize: "1rem",
                                lineHeight: 1.8,
                                color: "#d1d5db",
                                marginBottom: "18px",
                            }}
                        >
                            After reviewing the evaluation outcomes,{" "}
                            <strong style={{ color: "#ffffff" }}>
                                V K Chaudhury (ED-CIO)
                            </strong>{" "}
                            expressed his appreciation for the depth and seriousness with which
                            employees had engaged with the program. The participating teams together
                            estimated a potential ROI of{" "}
                            <strong style={{ color: "#cc2e29ff" }}>
                                ~₹22,400 Cr over five years
                            </strong>{" "}
                            from implementing all submitted use cases.
                        </p>

                        <div
                            style={{
                                background: "#111",
                                border: "1px solid #1e3a1e",
                                borderRadius: "8px",
                                padding: "16px 20px",
                                borderLeft: "3px solid #4ade80",
                            }}
                        >
                            <p
                                style={{
                                    fontSize: "0.95rem",
                                    color: "#86efac",
                                    lineHeight: 1.8,
                                    margin: 0,
                                }}
                            >
                                What was equally encouraging was that{" "}
                                <strong>
                                    20% of ideas were submitted with some groundwork already done
                                </strong>
                                . One in five teams did not just submit an idea, they had already begun
                                building. This shift from ideation to an execution mindset is the most
                                powerful outcome of this program, and it is happening organically.
                            </p>
                        </div>
                    </div>
                </section>

                <section
                    style={{
                        padding: "40px 16px",
                        borderTop: "1px solid #111",
                        background: "#000000",
                    }}
                >
                    <div style={{ marginTop: "20px" }}>
                        {/* HEADER */}
                        <div style={{ marginBottom: "28px", textAlign: "center" }}>
                            <h3
                                style={{
                                    color: "#4ade80",
                                    fontSize: "clamp(1.5rem, 3vw, 2rem)",
                                    marginTop: "10px",
                                    marginBottom: "16px",
                                    fontWeight: 800,
                                    lineHeight: 1.3,
                                }}
                            >
                                Recognition: Why Precious Stones, and What They Mean
                            </h3>

                            <p
                                style={{
                                    fontSize: "0.9rem",
                                    color: "#9ca3af",
                                    lineHeight: 1.8,
                                    maxWidth: "900px",
                                    margin: "0 auto",
                                }}
                            >
                                Every idea submitted to Udbhav 2.0 has been evaluated and assigned to
                                one of seven recognition categories, each named after a precious stone.
                                This was a deliberate design choice.
                            </p>

                            <p
                                style={{
                                    fontSize: "0.9rem",
                                    color: "#9ca3af",
                                    lineHeight: 1.8,
                                    maxWidth: "900px",
                                    margin: "18px auto 0",
                                }}
                            >
                                Precious stones carry no inherent rank. The intent is simple:
                                recognition is equal, classification reflects potential.
                            </p>
                        </div>

                        {/* GEM STYLES */}
                        {(() => {
                            const gemStyles = {
                                Diamond: { accent: "#60a5fa", glow: "rgba(96,165,250,0.15)" },
                                Ruby: { accent: "#ef4444", glow: "rgba(239,68,68,0.15)" },
                                Sapphire: { accent: "#6366f1", glow: "rgba(99,102,241,0.15)" },
                                Emerald: { accent: "#22c55e", glow: "rgba(34,197,94,0.15)" },
                                Topaz: { accent: "#f59e0b", glow: "rgba(245,158,11,0.15)" },
                                Jasper: { accent: "#a855f7", glow: "rgba(168,85,247,0.15)" },
                                Amber: { accent: "#f97316", glow: "rgba(249,115,22,0.15)" },
                            };

                            const gems = [
                                {
                                    gem: "Diamond",
                                    title: "High-impact, executable, transformational ideas",
                                    desc: "Clear AI application, strong business case, immediate implementation potential at scale.",
                                },
                                {
                                    gem: "Ruby",
                                    title: "Sharp, relevant, and ONGC-focused",
                                    desc: "Operationally strong ideas close to execution readiness.",
                                },
                                {
                                    gem: "Sapphire",
                                    title: "Strong foundation, needs shaping",
                                    desc: "Good problem understanding, needs refinement in AI or business framing.",
                                },
                                {
                                    gem: "Emerald",
                                    title: "Everyday problems solved smartly",
                                    desc: "Practical ideas addressing real operational friction.",
                                },
                                {
                                    gem: "Topaz",
                                    title: "Innovative, needs refinement",
                                    desc: "Creative ideas requiring feasibility structuring.",
                                },
                                {
                                    gem: "Jasper",
                                    title: "Out-of-the-box thinking",
                                    desc: "Experimental ideas worth preserving for future innovation.",
                                },
                                {
                                    gem: "Amber",
                                    title: "Acknowledgement for participation",
                                    desc: "Every submission is valued equally.",
                                },
                            ];

                            return (
                                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                                    {gems.map((g, i) => {
                                        const style = gemStyles[g.gem];

                                        return (
                                            <div
                                                key={i}
                                                style={{
                                                    background: `linear-gradient(180deg, ${style.glow}, #0b0b0f 45%)`,
                                                    border: "1px solid rgba(255,255,255,0.06)",
                                                    borderRadius: "14px",
                                                    padding: "16px",
                                                }}
                                            >
                                                {/* GRID */}
                                                <div
                                                    className="gem-grid"
                                                    style={{
                                                        display: "grid",
                                                        gridTemplateColumns: "minmax(0, 140px) 1fr",
                                                        gap: "16px",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    {/* BADGE */}
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            justifyContent: "center",
                                                            alignItems: "center",
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                padding: "9px 14px",
                                                                borderRadius: "999px",
                                                                border: `1px solid ${style.accent}`,
                                                                color: style.accent,
                                                                fontWeight: 900,
                                                                fontSize: "0.8rem",
                                                                letterSpacing: "0.6px",
                                                                textTransform: "uppercase",
                                                                background:
                                                                    "linear-gradient(180deg, rgba(255,255,255,0.03), rgba(0,0,0,0.25))",
                                                                boxShadow:
                                                                    "0 0 0 1px rgba(255,255,255,0.04) inset, 0 4px 12px rgba(0,0,0,0.3)",
                                                                textAlign: "center",
                                                                width: "100%",
                                                            }}
                                                        >
                                                            {g.gem}
                                                        </div>
                                                    </div>

                                                    {/* CONTENT */}
                                                    <div style={{ textAlign: "left" }}>
                                                        <div
                                                            style={{
                                                                fontSize: "0.9rem",
                                                                fontWeight: 600,
                                                                color: "#ffffff",
                                                                marginBottom: "6px",
                                                            }}
                                                        >
                                                            {g.title}
                                                        </div>

                                                        <div
                                                            style={{
                                                                fontSize: "0.85rem",
                                                                color: "#9ca3af",
                                                                lineHeight: 1.6,
                                                            }}
                                                        >
                                                            {g.desc}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {/* MOBILE FIX */}
                                    <style>{`
            @media (max-width: 640px) {
              .gem-grid {
                grid-template-columns: 1fr !important;
                text-align: center;
              }

              .gem-grid > div:last-child {
                text-align: center;
              }

              .gem-grid > div:first-child {
                margin-bottom: 10px;
              }
            }
          `}</style>
                                </div>
                            );
                        })()}

                        {/* CRITERIA BOX */}
                        <div
                            style={{
                                marginTop: "40px",
                                background: "#0a0a0a",
                                border: "1px solid #1a1a1a",
                                borderRadius: "12px",
                                padding: "20px",
                            }}
                        >
                            <h3
                                style={{
                                    fontSize: "1rem",
                                    color: "#4ade80",
                                    marginBottom: "16px",
                                    fontWeight: 700,
                                }}
                            >
                                Ideas were evaluated holistically against three core criteria:
                            </h3>

                            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                                {[
                                    "Alignment with ONGC's KPIs of safety, environment, sustainability, cost reduction, or revenue growth.",
                                    "The business use case had to be specific and operationally relevant.",
                                    "Each idea had to be implementable within the program duration.",
                                ].map((item, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            display: "flex",
                                            gap: "10px",
                                            alignItems: "flex-start",
                                        }}
                                    >
                                        <div
                                            style={{
                                                width: "6px",
                                                height: "6px",
                                                borderRadius: "50%",
                                                background: "#4ade80",
                                                marginTop: "8px",
                                            }}
                                        />
                                        <p
                                            style={{
                                                fontSize: "0.9rem",
                                                color: "#d1d5db",
                                                lineHeight: 1.6,
                                                margin: 0,
                                            }}
                                        >
                                            {item}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* IMAGE */}
                        <div
                            style={{
                                marginTop: "28px",
                                background: "#0a0a0a",
                                border: "1px solid #1a1a1a",
                                borderRadius: "12px",
                                padding: "16px",
                            }}
                        >
                            <img
                                src="/images/image6artical.png"
                                alt="Udbhav 2.0 Evaluation Overview"
                                style={{
                                    width: "100%",
                                    borderRadius: "8px",
                                    display: "block",
                                }}
                            />
                        </div>
                    </div>
                </section>
                {/* DIGITAL CERTIFICATES */}
                <section style={{ padding: "32px 0", borderTop: "1px solid #111" }}>
                    <div style={{ textAlign: "center", marginBottom: "28px" }}>
                        {/* <span style={{ fontSize: "14px", letterSpacing: "3px", color: "#f87171", fontWeight: "bold" }}>Recognition</span> */}
                        <h2 style={{
                            fontSize: "clamp(1.5rem, 3vw, 2.2rem)",
                            fontWeight: 800,
                            margin: "8px 0 0",
                            color: "#4ade80",
                            letterSpacing: "-0.02em",
                        }}>
                            Digital Certificates: Recognizing Every Participant
                        </h2>
                    </div>

                    <div style={{
                        background: "#0a0a0a",
                        border: "1px solid #1a1a1a",
                        borderRadius: "12px",
                        padding: "28px",
                    }}>
                        <p style={{ fontSize: "1rem", lineHeight: 1.6, color: "#d1d5db", marginBottom: "16px" }}>
                            Every team that submitted an idea to Udbhav 2.0 has received a <strong style={{ color: "#fff" }}>personalized digital certificate</strong> corresponding to their award category. These certificates have been emailed to all participating teams' members.
                        </p>
                        <p style={{ fontSize: "1rem", lineHeight: 1.6, color: "#9ca3af", marginBottom: "24px" }}>
                            Submitting an idea to Udbhav — especially one that has been evaluated and categorized — represents real intellectual effort, real domain knowledge, and a real belief in ONGC's innovation future. Every one of those contributions deserves to be recognized.
                        </p>

                        {/* ED-CIO Quote */}
                        <div style={{
                            background: "#050505",
                            border: "1px solid #1e3a1e",
                            borderRadius: "12px",
                            padding: "24px",
                            position: "relative",
                        }}>
                            <p style={{
                                fontSize: "1rem",
                                lineHeight: 1.6,
                                color: "#d1d5db",
                                margin: "0 0 12px",
                                fontStyle: "italic",
                            }}>
                                To every ONGCian who submitted an idea: thank you. You looked at a problem you face every day and asked whether AI could help. That question, asked 542 times across this organization, is what drives ONGC's transformation forward.
                            </p>
                            <p style={{
                                fontSize: "0.95rem",
                                fontStyle: "italic",
                                color: "#cc2525ff",
                                margin: "0 0 8px",
                                fontWeight: "600",
                            }}>
                                The idea you submitted is not the end of your contribution. It may just be the beginning.
                            </p>
                            <p style={{
                                fontSize: "0.85rem",
                                color: "#9ca3af",
                                margin: 0,
                                fontWeight: "600",
                            }}>
                                — V K Chaudhury, ED-CIO
                            </p>
                        </div>
                    </div>
                </section>



            </main>
        </div>
    );
}